import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import { makeAuthenticatedUploadRequestWithAbort } from "../services/tokenHelper";
import { mediaService } from "../services/mediaService";
import {
  Media,
  MediaDuplicateFingerprint,
  MediaDuplicateFingerprints,
  MultipleMediaUploadResult,
} from "../types/media";
import { mediaKeys } from "../hooks/useMediaQuery";
import { albumKeys } from "../hooks/useAlbumsQuery";
import { DEFAULT_MEDIA_SORT } from "../types/mediaSort";
import { getUploadDeviceInfo } from "../utils/uploadDeviceInfo";
import { duplicateNameSizeKey, isMediaDuplicate } from "../utils/mediaDuplicate";
import { extractMediaTakenAt } from "../utils/extractMediaTakenAt";
import { useWakeLock } from "../hooks/useWakeLock";
import {
  PERSIST_WARNING,
  deleteUploadBlob,
  hydratePersistedQueue,
  persistUploadBlob,
  requestPersistentStorage,
  saveQueueMetadata,
  toPersistedMeta,
} from "../utils/uploadQueueStore";

export type UploadItemStatus = "queued" | "uploading" | "success" | "failed" | "cancelled" | "duplicate";

export type UploadQueueItem = {
  id: string;
  ownerId: string;
  albumId: string;
  file: File;
  fileName: string;
  fileSize: number;
  status: UploadItemStatus;
  percent: number; // 0..100
  error?: string;
  startedAt?: number;
  finishedAt?: number;
  takenAt?: string;
  persisted?: boolean;
};

type EnqueueArgs = {
  ownerId: string;
  albumId: string;
  files: File[];
};

type UploadQueueContextValue = {
  items: UploadQueueItem[];
  activeCount: number;
  enqueue: (args: EnqueueArgs) => string[]; // returns item ids
  cancel: (id: string) => void;
  retry: (id: string) => void;
  retryAllFailed: () => void;
  abortAll: () => void;
  resumeCancelled: () => void;
  dismiss: () => void;
  isVisible: boolean;
  show: () => void;
  persistWarning: string | null;
};

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function makeId(): string {
  // Non-crypto ID is fine here; it’s only for UI identity.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function requeueItem(it: UploadQueueItem): UploadQueueItem {
  return {
    ...it,
    status: "queued",
    percent: 0,
    error: undefined,
    startedAt: undefined,
    finishedAt: undefined,
  };
}

function isTerminalStatus(status: UploadItemStatus): boolean {
  return status === "success" || status === "failed" || status === "cancelled" || status === "duplicate";
}

function toFingerprint(item: UploadQueueItem): MediaDuplicateFingerprint {
  return {
    filename: item.fileName,
    originalSize: item.fileSize,
    takenAt: item.takenAt,
  };
}

async function uploadSingleFile(
  ownerId: string,
  albumId: string,
  file: File,
  onProgress?: (loaded: number, total?: number) => void
): Promise<{ promise: Promise<MultipleMediaUploadResult>; abort: () => void }> {
  const deviceInfo = await getUploadDeviceInfo();
  const formData = new FormData();
  formData.append("files", file);
  if (deviceInfo.uploadDeviceModel) formData.append("uploadDeviceModel", deviceInfo.uploadDeviceModel);
  if (deviceInfo.uploadDevicePlatform) formData.append("uploadDevicePlatform", deviceInfo.uploadDevicePlatform);
  if (deviceInfo.uploadDevicePlatformVersion) {
    formData.append("uploadDevicePlatformVersion", deviceInfo.uploadDevicePlatformVersion);
  }
  const { promise, abort } = await makeAuthenticatedUploadRequestWithAbort<MultipleMediaUploadResult>(
    `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media`,
    formData,
    ({ loaded, total }) => onProgress?.(loaded, total)
  );
  return { promise, abort };
}

export function UploadQueueProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [persistWarning, setPersistWarning] = useState<string | null>(null);

  // Track “cancel” intent for queued items, and abort current XHR when possible.
  const abortCurrentRef = useRef<null | (() => void)>(null);
  const currentUploadingIdRef = useRef<string | null>(null);
  const itemsRef = useRef<UploadQueueItem[]>([]);
  itemsRef.current = items;

  const activeCount = useMemo(
    () => items.filter((i) => i.status === "queued" || i.status === "uploading").length,
    [items]
  );

  useWakeLock(activeCount > 0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const restored = await hydratePersistedQueue();
        if (cancelled) return;
        if (restored.items.length > 0) {
          setItems(restored.items);
          setIsVisible(true);
        }
        if (restored.persistWarning) {
          setPersistWarning(restored.persistWarning);
        }
      } catch {
        // A corrupt store should not block new uploads.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const timer = window.setTimeout(() => {
      void saveQueueMetadata(items.map(toPersistedMeta));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [hydrated, items]);

  useEffect(() => {
    if (!hydrated) return;
    for (const item of items) {
      if (item.status === "success" || item.status === "duplicate" || item.status === "cancelled") {
        void deleteUploadBlob(item.id);
      }
    }
  }, [hydrated, items]);

  const prefetchFingerprints = useCallback(
    (ownerId: string, albumId: string) => {
      void queryClient.prefetchQuery({
        queryKey: mediaKeys.duplicateFingerprints(ownerId, albumId),
        queryFn: () => mediaService.listDuplicateFingerprints(ownerId, albumId),
      });
    },
    [queryClient]
  );

  const enqueue = useCallback(
    (args: EnqueueArgs) => {
      const ids: string[] = [];
      const pendingPersist: UploadQueueItem[] = [];
      setItems((prev) => {
        const next: UploadQueueItem[] = [...prev];
        const seen = new Set(
          prev
            .filter((item) => item.ownerId === args.ownerId && item.albumId === args.albumId)
            .filter(
              (item) =>
                item.status === "queued" ||
                item.status === "uploading" ||
                item.status === "success" ||
                item.status === "duplicate"
            )
            .map((item) => duplicateNameSizeKey(item.fileName, item.fileSize))
        );
        for (const file of args.files) {
          const id = makeId();
          ids.push(id);
          const fileName = file.name || "untitled";
          const fileSize = Number.isFinite(file.size) ? file.size : 0;
          const key = duplicateNameSizeKey(fileName, fileSize);
          const duplicateInBatch = seen.has(key);
          if (!duplicateInBatch) {
            seen.add(key);
          }
          const created: UploadQueueItem = {
            id,
            ownerId: args.ownerId,
            albumId: args.albumId,
            file,
            fileName,
            fileSize,
            status: duplicateInBatch ? "duplicate" : "queued",
            percent: 0,
            finishedAt: duplicateInBatch ? Date.now() : undefined,
            persisted: false,
          };
          next.push(created);
          if (!duplicateInBatch) {
            pendingPersist.push(created);
          }
        }
        return next;
      });

      void (async () => {
        await requestPersistentStorage();
        let anyFailed = false;
        for (const item of pendingPersist) {
          const ok = await persistUploadBlob(item.id, item.file);
          setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, persisted: ok } : it)));
          if (!ok) anyFailed = true;
        }
        if (anyFailed) {
          setPersistWarning(PERSIST_WARNING);
        }
      })();

      prefetchFingerprints(args.ownerId, args.albumId);
      setIsVisible(true);
      return ids;
    },
    [prefetchFingerprints]
  );

  const cancel = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        if (isTerminalStatus(it.status)) return it;
        return { ...it, status: "cancelled", finishedAt: Date.now() };
      })
    );

    if (currentUploadingIdRef.current === id) {
      abortCurrentRef.current?.();
    }
  }, []);

  const retry = useCallback((id: string) => {
    setItems((prev) => {
      const target = prev.find((it) => it.id === id);
      if (!target || (target.status !== "failed" && target.status !== "cancelled")) return prev;
      return [...prev.filter((it) => it.id !== id), requeueItem(target)];
    });
    setIsVisible(true);
  }, []);

  const retryAllFailed = useCallback(() => {
    setItems((prev) => {
      const failed = prev.filter((it) => it.status === "failed");
      if (failed.length === 0) return prev;
      return [...prev.filter((it) => it.status !== "failed"), ...failed.map(requeueItem)];
    });
    setIsVisible(true);
  }, []);

  const abortAll = useCallback(() => {
    setItems((prev) =>
      prev.map((it) =>
        it.status === "queued" || it.status === "uploading"
          ? { ...it, status: "cancelled", finishedAt: Date.now() }
          : it
      )
    );
    abortCurrentRef.current?.();
  }, []);

  const resumeCancelled = useCallback(() => {
    setItems((prev) => {
      const cancelled = prev.filter((it) => it.status === "cancelled");
      if (cancelled.length === 0) return prev;
      return [...prev.filter((it) => it.status !== "cancelled"), ...cancelled.map(requeueItem)];
    });
    setIsVisible(true);
  }, []);

  const dismiss = useCallback(() => setIsVisible(false), []);
  const show = useCallback(() => setIsVisible(true), []);

  // If the provider unmounts (navigation away / refresh), abort any in-flight upload.
  React.useEffect(() => {
    return () => {
      abortCurrentRef.current?.();
    };
  }, []);

  // “Worker loop”: whenever items change, ensure exactly one upload runs at a time.
  useEffect(() => {
    // IMPORTANT: This effect runs on every progress update (items state change).
    // We must not "cancel" an in-flight upload just because React re-ran the effect.
    // Use refs to track whether an upload is already running.
    if (!hydrated) return;
    if (currentUploadingIdRef.current) return;

    const next = items.find((i) => i.status === "queued");
    if (!next) return;

    currentUploadingIdRef.current = next.id;

    (async () => {
      try {
        let albumFingerprints: MediaDuplicateFingerprint[] = [];
        try {
          const fingerprints = await queryClient.fetchQuery({
            queryKey: mediaKeys.duplicateFingerprints(next.ownerId, next.albumId),
            queryFn: () => mediaService.listDuplicateFingerprints(next.ownerId, next.albumId),
            staleTime: 60_000,
          });
          albumFingerprints = fingerprints.items ?? [];
        } catch {
          albumFingerprints = [];
        }

        const otherFingerprints = itemsRef.current
          .filter((item) => item.id !== next.id && item.ownerId === next.ownerId && item.albumId === next.albumId)
          .filter(
            (item) =>
              item.status === "success" ||
              item.status === "duplicate" ||
              item.status === "uploading" ||
              item.status === "queued"
          )
          .map(toFingerprint);
        const nameSizeHits = [...albumFingerprints, ...otherFingerprints].filter(
          (fingerprint) =>
            duplicateNameSizeKey(fingerprint.filename, fingerprint.originalSize) ===
            duplicateNameSizeKey(next.fileName, next.fileSize)
        );

        let takenAt = next.takenAt;
        if (nameSizeHits.length > 0) {
          takenAt = takenAt ?? (await extractMediaTakenAt(next.file));
          const incoming = {
            filename: next.fileName,
            originalSize: next.fileSize,
            takenAt,
          };
          if (nameSizeHits.some((fingerprint) => isMediaDuplicate(incoming, fingerprint))) {
            setItems((prev) =>
              prev.map((it) =>
                it.id === next.id
                  ? it.status === "cancelled"
                    ? it
                    : { ...it, status: "duplicate", takenAt, finishedAt: Date.now() }
                  : it
              )
            );
            return;
          }
        }

        if (itemsRef.current.find((item) => item.id === next.id)?.status === "cancelled") {
          return;
        }

        setItems((prev) =>
          prev.map((it) =>
            it.id === next.id
              ? it.status === "cancelled"
                ? it
                : { ...it, status: "uploading", startedAt: Date.now(), percent: 0, takenAt }
              : it
          )
        );

        const { promise, abort } = await uploadSingleFile(
          next.ownerId,
          next.albumId,
          next.file,
          (loaded, totalFromEvent) => {
            const total = typeof totalFromEvent === "number" && totalFromEvent > 0 ? totalFromEvent : next.fileSize;
            const pct = total > 0 ? (loaded / total) * 100 : 0;
            setItems((prev) => prev.map((it) => (it.id === next.id ? { ...it, percent: clampPercent(pct) } : it)));
          }
        );
        abortCurrentRef.current = abort;
        const result = await promise;

        const duplicateFromServer = (result.duplicates || []).length > 0 && (result.successful || []).length === 0;
        if (duplicateFromServer) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === next.id
                ? it.status === "cancelled"
                  ? it
                  : { ...it, status: "duplicate", percent: 0, finishedAt: Date.now() }
                : it
            )
          );
          return;
        }

        const wasSuccessful = (result.successful || []).length > 0 && (result.failed || []).length === 0;
        if (!wasSuccessful) {
          const message = (result.failed && result.failed[0]?.error) || "Upload failed";
          throw new Error(message);
        }

        const uploadedMedia = result.successful || [];

        queryClient.setQueryData<MediaDuplicateFingerprints>(
          mediaKeys.duplicateFingerprints(next.ownerId, next.albumId),
          (old) => {
            const existingItems = old?.items ?? [];
            const added = uploadedMedia.map((media) => ({
              filename: media.filename,
              originalSize: media.originalSize ?? next.fileSize,
              takenAt: media.metadata?.takenAt,
            }));
            return { items: [...existingItems, ...added] };
          }
        );

        // Sorting is configurable; safest is to refetch all cached media lists for this album.
        // (We still touch the default key so the first paint stays snappy if the user is on the default sort.)
        queryClient.setQueryData<InfiniteData<Media[]>>(
          mediaKeys.list(next.ownerId, next.albumId, DEFAULT_MEDIA_SORT),
          (old) => old ?? { pages: [uploadedMedia], pageParams: [0] }
        );
        queryClient.invalidateQueries({
          queryKey: mediaKeys.listBase(next.ownerId, next.albumId),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: mediaKeys.indexBase(next.ownerId, next.albumId),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: mediaKeys.uploaders(next.ownerId, next.albumId),
        });

        // Invalidate album detail (mediaCount) + albums list (thumbnails)
        queryClient.invalidateQueries({ queryKey: albumKeys.detail(next.ownerId, next.albumId) });
        queryClient.invalidateQueries({ queryKey: albumKeys.list(next.ownerId) });

        setItems((prev) =>
          prev.map((it) =>
            it.id === next.id
              ? it.status === "cancelled"
                ? it
                : { ...it, status: "success", percent: 100, finishedAt: Date.now() }
              : it
          )
        );
      } catch (e) {
        const message = e instanceof Error ? e.message : "Upload failed";
        setItems((prev) =>
          prev.map((it) =>
            it.id === next.id
              ? it.status === "cancelled"
                ? it
                : { ...it, status: "failed", error: message, finishedAt: Date.now() }
              : it
          )
        );
      } finally {
        currentUploadingIdRef.current = null;
        abortCurrentRef.current = null;
      }
    })();
  }, [hydrated, items, queryClient]);

  const value: UploadQueueContextValue = useMemo(
    () => ({
      items,
      activeCount,
      enqueue,
      cancel,
      retry,
      retryAllFailed,
      abortAll,
      resumeCancelled,
      dismiss,
      isVisible,
      show,
      persistWarning,
    }),
    [
      items,
      activeCount,
      enqueue,
      cancel,
      retry,
      retryAllFailed,
      abortAll,
      resumeCancelled,
      dismiss,
      isVisible,
      show,
      persistWarning,
    ]
  );

  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUploadQueue(): UploadQueueContextValue {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) throw new Error("useUploadQueue must be used within UploadQueueProvider");
  return ctx;
}
